import type { TFunction } from 'i18next'
import type { CalcError } from '@rollcall/contracts'
import { CalculationFailed } from './api.js'

/**
 * Причина, по которой лист не посчитался, — человеческой строкой.
 *
 * Живёт отдельно от экрана: то же сообщение нужно и листу, и визарду,
 * и вкладке боя, а собирается оно одинаково — по коду и полям ошибки
 * (D11), а не по тексту с сервера.
 *
 * Зачем это вообще: до этой правки любая ошибка расчёта приезжала
 * на экран как «сервер недоступен» при работающей сети. Причина
 * не называлась вовсе, и чинить по такому сообщению было нечего.
 */

/** Подстановки для строки словаря: у каждого кода они свои. */
function valuesOf(detail: CalcError): Record<string, string | number> {
  switch (detail.code) {
    case 'unknown-path':
    case 'unexpected-context-value':
      return { path: detail.path }
    case 'cyclic-ref':
      return { chain: detail.chain.join(' → ') }
    case 'conflicting-set':
    case 'missing-base':
    case 'unexpected-target-kind':
      return { target: detail.target }
    case 'duplicate-step':
      return { stepIndex: detail.stepIndex }
    case 'unknown-content':
      return { id: detail.id }
  }
}

/**
 * Заголовок и причина одной строкой.
 *
 * Незнакомый код — не пустота: сервер может выпустить новый код
 * раньше, чем клиент про него узнает, и «лист не посчитался»
 * без причины всё равно лучше, чем «сервер недоступен».
 */
export function calcErrorText(t: TFunction, detail: CalcError): string {
  const title = t('errors.calculation.title')
  const key = `errors.calculation.${detail.code}`
  const reason = t(key, valuesOf(detail))
  return reason === key
    ? `${title}. ${t('errors.calculation.unknown')}`
    : `${title}. ${reason}`
}

/**
 * Текст любой неудачи запроса: ошибка расчёта — своим сообщением,
 * всё остальное — общим.
 *
 * Три случая, и они не сводятся друг к другу: «нет такого» —
 * устаревшая или чужая ссылка, «не посчитался» — сеть в порядке,
 * а данные не сходятся, «недоступен» — ждать.
 */
export function failureText(t: TFunction, error: unknown, fallback: string): string {
  return error instanceof CalculationFailed ? calcErrorText(t, error.detail) : fallback
}
